import { useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
} from '@mui/material'
import { AccountTree } from '@mui/icons-material'

import { useGetAttributionQuery, useGetProjectConfigQuery } from '@/apis/v2Api'
import { EmptyState } from '@/components/shared/EmptyState'
import { useReportArgs } from '@/hooks/useReportArgs'
import type { Period } from '@/utils/dates'

import { Kpi, ReportHeader, ReportStatus, SectionCard, fmtMoney, fmtNum, fmtPct } from './common'
import { EVENT_NAME_RE } from './MatcherFields'

type Model = 'any_touch' | 'first_touch' | 'last_touch'

interface Params {
    conversion: string
    touch: string
    model: Model
    windowDays: number
}

const MODELS: Array<{ value: Model; label: string }> = [
    { value: 'last_touch', label: 'Last touch' },
    { value: 'first_touch', label: 'First touch' },
    { value: 'any_touch', label: 'Any touch' },
]

function KeyTable({
    title,
    keyLabel,
    rows,
    currency,
}: {
    title: string
    keyLabel: string
    rows: Array<{ key: string; conversions: number; value: number }>
    currency: string
}) {
    return (
        <SectionCard title={title}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>{keyLabel}</TableCell>
                        <TableCell align="right">Conversions</TableCell>
                        <TableCell align="right">Value</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map(r => (
                        <TableRow key={r.key} hover>
                            <TableCell>{r.key}</TableCell>
                            <TableCell align="right">{fmtNum(r.conversions, 1)}</TableCell>
                            <TableCell align="right">{fmtMoney(r.value, currency)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </SectionCard>
    )
}

/** v2 `/reports/attribution` — conversions preceded by a touch event within a window. */
export function AttributionPage() {
    const [period, setPeriod] = useState<Period>('30d')
    const args = useReportArgs(period)
    const [form, setForm] = useState<Params>({
        conversion: 'purchase',
        touch: '',
        model: 'last_touch',
        windowDays: 7,
    })
    const [submitted, setSubmitted] = useState<Params | null>(null)
    const { data: cfg } = useGetProjectConfigQuery(args.projectId)
    const { data, isFetching, error } = useGetAttributionQuery(
        { ...args, ...(submitted ?? form) },
        { skip: !submitted },
    )
    const valid = EVENT_NAME_RE.test(form.conversion) && EVENT_NAME_RE.test(form.touch)
    const currency = cfg?.config.currency ?? 'USD'

    return (
        <Box sx={{ p: 3 }}>
            <ReportHeader
                title="Attribution"
                subtitle="Which touch (e.g. a widget interaction or campaign event) preceded each conversion."
                period={period}
                onPeriodChange={setPeriod}
            />
            <Card sx={{ mb: 3 }}>
                <CardContent
                    component="form"
                    onSubmit={e => {
                        e.preventDefault()
                        if (valid) {
                            setSubmitted(form)
                        }
                    }}
                    sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}
                >
                    <TextField
                        size="small"
                        label="Conversion event"
                        value={form.conversion}
                        onChange={e => setForm({ ...form, conversion: e.target.value.trim() })}
                    />
                    <TextField
                        size="small"
                        label="Touch event"
                        required
                        value={form.touch}
                        placeholder="e.g. cta_click"
                        onChange={e => setForm({ ...form, touch: e.target.value.trim() })}
                    />
                    <TextField
                        size="small"
                        select
                        label="Model"
                        value={form.model}
                        onChange={e => setForm({ ...form, model: e.target.value as Model })}
                        sx={{ width: 150 }}
                    >
                        {MODELS.map(m => (
                            <MenuItem key={m.value} value={m.value}>
                                {m.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        size="small"
                        type="number"
                        label="Window (days)"
                        value={form.windowDays}
                        onChange={e =>
                            setForm({
                                ...form,
                                windowDays: Math.min(90, Math.max(1, Number(e.target.value) || 1)),
                            })
                        }
                        sx={{ width: 130 }}
                    />
                    <Button type="submit" variant="contained" disabled={!valid}>
                        Run
                    </Button>
                </CardContent>
            </Card>

            {!submitted ? (
                <EmptyState
                    icon={<AccountTree />}
                    title="Choose a conversion and a touch event"
                    description="Attribution credits each conversion to the touch event (and its channel / label / item) that preceded it within the window."
                />
            ) : (
                <ReportStatus
                    isLoading={isFetching}
                    error={error}
                    isEmpty={!!data && data.conversions === 0}
                    emptyIcon={<AccountTree />}
                    emptyTitle={`No ${submitted.conversion} events in this range`}
                >
                    {data && (
                        <>
                            {data.truncated && (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    Too many rows — results cover a truncated sample. Narrow the
                                    range.
                                </Alert>
                            )}
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid size={{ xs: 6, md: 3 }}>
                                    <Kpi
                                        title="Conversions"
                                        value={fmtNum(data.conversions)}
                                        exact={!!(data.exact_conversions || data.exact)}
                                        estimate={!data.exact_conversions && data.exact === false}
                                    />
                                </Grid>
                                <Grid size={{ xs: 6, md: 3 }}>
                                    <Kpi
                                        title="Attributed"
                                        value={fmtNum(data.attributed_conversions)}
                                        estimate={data.exact === false}
                                    />
                                </Grid>
                                <Grid size={{ xs: 6, md: 3 }}>
                                    <Kpi
                                        title="Attributed share"
                                        value={fmtPct(data.attributed_share)}
                                        estimate={data.exact === false}
                                    />
                                </Grid>
                                <Grid size={{ xs: 6, md: 3 }}>
                                    <Kpi
                                        title="Attributed value"
                                        value={fmtMoney(data.attributed_value, currency)}
                                        estimate={data.exact === false}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <KeyTable
                                        title="By channel"
                                        keyLabel="Channel"
                                        rows={data.by_channel}
                                        currency={currency}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <KeyTable
                                        title="By touch (item / label)"
                                        keyLabel="Touch"
                                        rows={data.by_touch}
                                        currency={currency}
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )}
                </ReportStatus>
            )}
        </Box>
    )
}
